import { isPhotonAppRoute } from "@/components/Auth/shared/constants.auth";
import CustomMenu from "@/components/Menu";
import useAppNavigate from "@/hooks/useAppNavigate";
import { useLocation, useNavigate } from "@/lib/router";
import { cn } from "@/utils/cn";
import { getTwoLetterOrInitials } from "@/utils/initials";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useIntercom } from "react-use-intercom";
import { useAuth, useUser } from "reactfire";
import {
  photonProfileMenuItems,
  profileMenuIcons,
  profileMenuItems,
  type ProfileMenuAction,
} from "../config.nav";
import { CHECKOUT_SUMMARY_QUERY_KEY } from "@/components/Checkout/hooks";
import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";

type TProps = {
  darkTheme?: boolean;
};

export const NavProfile = (props: TProps) => {
  const { darkTheme = true } = props;

  const navigate = useNavigate();
  const navigateTo = useAppNavigate();
  const location = useLocation();

  const { data } = useUser();
  const auth = useAuth();
  const { show } = useIntercom();
  const client = useQueryClient();

  const actionHandlers: Record<ProfileMenuAction, () => void> = {
    help: () => show(),
    logout: () => {
      auth.signOut();
      toast.success("Logged out successfully");
      setTimeout(() => {
        client.removeQueries({ queryKey: ["user"] });
        client.removeQueries({ queryKey: [USER_PLAN_QUERY_KEY] });
        client.removeQueries({ queryKey: ["pricing-data"] });
        client.removeQueries({ queryKey: [CHECKOUT_SUMMARY_QUERY_KEY] });
      }, 600);
    },
  };

  const items = isPhotonAppRoute(location.pathname)
    ? photonProfileMenuItems
    : profileMenuItems;

  const menuOptions = items.map((item) => {
    const IconComponent = profileMenuIcons[item.icon];

    let onClick: (() => void) | undefined;
    if (item.action) {
      onClick = actionHandlers[item.action];
    } else if (item.tab) {
      onClick = () => navigate(`/?tab=${item.tab}`);
    } else if (item.path) {
      onClick = () => navigateTo(item.path!);
    }

    let disabled = false;
    if (item.disableWhen === "not-password-provider") {
      disabled = data?.providerData[0].providerId !== "password";
    }

    return {
      icon: <IconComponent className="h-5 w-5" />,
      text: item.text,
      onClick,
      url: item.url,
      disabled,
      hoverClass: item.hoverClass,
    };
  });

  return (
    <div className="flex min-w-25 shrink-0">
      <CustomMenu options={menuOptions} className="ml-auto">
        {data?.photoURL ? (
          <img
            src={data.photoURL}
            alt="avatar"
            width={40}
            height={40}
            className="h-8 w-8 rounded-full ring-2 ring-edge-accent md:h-10 md:w-10"
          />
        ) : (
          <div
            className={cn(
              "group relative flex h-8 w-8 items-center justify-center truncate rounded-full text-lg text-type-primary uppercase md:h-10 md:w-10 md:text-xl",
              "bg-accent ring-2 ring-edge-accent",
            )}
          >
            {getTwoLetterOrInitials(data?.displayName || "")}
          </div>
        )}
      </CustomMenu>
    </div>
  );
};
